import { createContext, useCallback, useContext, useMemo, useState } from "react"
import { RegistrationDraft, getRegistrationDraft, updateRegistrationDraft, saveRegistrationDraft } from "@/utils/registration"

type RegistrationContextValue = {
  draft: RegistrationDraft
  setStep1Draft: (data: RegistrationDraft["step1"]) => void
  setProfileDraft: (data: RegistrationDraft["profile"]) => void
  markStep1Complete: () => void
  markStep2Complete: () => void
}

const RegistrationContext = createContext<RegistrationContextValue | null>(null)

export const RegistrationProvider = ({ children }: { children: React.ReactNode }) => {
  const [draft, setDraft] = useState<RegistrationDraft>(() => getRegistrationDraft())

  const setStep1Draft = useCallback((data: RegistrationDraft["step1"]) => {
    const next = updateRegistrationDraft({
      step1: data ? { ...data } : undefined
    })
    setDraft(next)
  }, [])

  const setProfileDraft = useCallback((data: RegistrationDraft["profile"]) => {
    const next = updateRegistrationDraft({
      profile: data ? { ...data } : undefined
    })
    setDraft(next)
  }, [])

  const markStep1Complete = useCallback(() => {
    const next = updateRegistrationDraft({ step1Completed: true })
    setDraft(next)
  }, [])

  const markStep2Complete = useCallback(() => {
    const next = updateRegistrationDraft({ step2Completed: true })
    setDraft(next)
  }, [])

  const value = useMemo(() => ({
    draft,
    setStep1Draft,
    setProfileDraft,
    markStep1Complete,
    markStep2Complete
  }), [draft, setStep1Draft, setProfileDraft, markStep1Complete, markStep2Complete])

  return (
    <RegistrationContext.Provider value={value}>
      {children}
    </RegistrationContext.Provider>
  )
}

export const useRegistration = () => {
  const context = useContext(RegistrationContext)
  if (!context) {
    const fallback = getRegistrationDraft()
    return {
      draft: fallback,
      setStep1Draft: (data: RegistrationDraft["step1"]) => {
        saveRegistrationDraft({ ...fallback, step1: data })
      },
      setProfileDraft: (data: RegistrationDraft["profile"]) => {
        saveRegistrationDraft({ ...fallback, profile: data })
      },
      markStep1Complete: () => {
        saveRegistrationDraft({ ...fallback, step1Completed: true })
      },
      markStep2Complete: () => {
        saveRegistrationDraft({ ...fallback, step2Completed: true })
      }
    }
  }
  return context
}
